import type {
  CartonComponentsResponse,
  CartonItemsResponse,
} from '@skupreme/api-contracts/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebouncedMemo } from '@hooks/debounceAsync';
import { CommonConstant } from '@utility/constants/common';
import { ComponentsService } from '@utility/services/components';
import ItemService from '@utility/services/item';
import { type CartonListParams, cartonKeys } from '../lib/query-keys';
import {
  type CartonKind,
  type CartonRow,
  fromComponent,
  fromItem,
} from '../lib/rows';

const noRows: CartonRow[] = [];

async function fetchRows(kind: CartonKind, params: CartonListParams) {
  if (kind === 'items') {
    const page: CartonItemsResponse = await ItemService.globalItems(params);
    return { rows: page.data.map(fromItem), total: page.total_data };
  }

  const page: CartonComponentsResponse =
    await ComponentsService.globalComponents(params);
  return { rows: page.data.map(fromComponent), total: page.total_data };
}

/* Items or components with their carton configurations, a page at a time. */
export function useCartonList(kind: CartonKind) {
  const [filter, setFilter] = useState<CartonListParams>({
    page: 1,
    limit: CommonConstant.defaultPageSize,
    search: '',
  });

  const params = { ...filter, search: filter.search.trim() };
  /* Typing in the search box asks once it settles, not on every key. */
  const applied = useDebouncedMemo<CartonListParams>(
    () => params,
    [JSON.stringify(params)],
    500,
  );

  const query = useQuery({
    queryKey: cartonKeys.list(kind, applied ?? params),
    queryFn: () => fetchRows(kind, applied ?? params),
    enabled: !!applied,
    placeholderData: keepPreviousData,
  });

  /* Any filter but the page starts the list over. */
  const filterChanged = <K extends keyof CartonListParams>(
    key: K,
    value: CartonListParams[K],
  ) => {
    setFilter(previous => ({
      ...previous,
      [key]: value,
      page: key === 'page' ? (value as number) : 1,
    }));
  };

  return {
    rows: query.data?.rows ?? noRows,
    loading: !applied || query.isPending,
    totalData: query.data?.total ?? 0,
    filter,
    filterChanged,
    refetch: query.refetch,
  };
}
